import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import authService from "../../../services/authService";
import logo from '../../../assets/icons/logo/Logo.svg';
import './styles.scss';

export default function VerificationCode({
    customMessages = {
        invalidCode: "Invalid verification code",
        required: "Verification code is required",
        codeExpired: "Verification code has expired",
        resendSuccess: "Verification code has been resent"
    }
}) {
    //  React Router hooks
    const location = useLocation(); // used to receive data passed via navigate() (e.g., email)
    const navigate = useNavigate(); // used for redirecting to another page
    const email = location.state?.email || ""; // user email (passed from previous page)

    // React state variables
    const [code, setCode] = useState(["", "", "", "", "", ""]); // stores the 6 digits entered by the user
    const [error, setError] = useState(""); // stores error messages (e.g. invalid code)
    const [isLoading, setIsLoading] = useState(false); // indicates when code verification is in progress
    const [countdown, setCountdown] = useState(60); // countdown timer for resending code
    const [canResend, setCanResend] = useState(false); // determines if user can resend code
    const [resendSuccess, setResendSuccess] = useState(false); // displays success message after resending
    const inputRefs = useRef([]); // references for each input field to manage focus

    useEffect(() => {
        if (!email) {
            navigate('/forgot-password', { replace: true });
        }
    }, [email, navigate]);

    //  Countdown effect
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true); 
        }
    }, [countdown]);

    //  Handles typing in each input field
    const handleCodeChange = (index, value) => {
        if (!/^\d?$/.test(value)) return; // allows only digits

        const newCode = [...code];
        newCode[index] = value; // update entered digit
        setCode(newCode);


        // auto move to next input when a digit is entered
        if (value && index < 5) {
            inputRefs.current[index + 1].focus();
        }

        setError(""); 
    };

    //  Move back on Backspace if input is empty
    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !code[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    //  Handle pasting entire code at once
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text');
        const pastedCode = pastedData.slice(0, 6).split('');
        
        if (pastedCode.every(char => /^\d?$/.test(char))) {
            const newCode = [...code];
            pastedCode.forEach((char, index) => {
                if (index < 6) newCode[index] = char;
            });
            setCode(newCode);
            
            const lastFilledIndex = Math.min(pastedCode.length - 1, 5);
            inputRefs.current[lastFilledIndex].focus();

        }
    };

    //  Validation for required fields
    const validateCode = () => {
        if (code.some(digit => digit === "")) {
            return customMessages.required;
        }
        return "";
    };

    //  Submit verification code
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const validationError = validateCode();
        if (validationError) {
            setError(validationError);
            console.warn("Validation error:", validationError);
            return;
        }

        setIsLoading(true);
        const verificationCode = code.join(''); 

      

        navigate('/reset-password', { 
            state: { 
                email: email,
                code: verificationCode
            }
        });

        setIsLoading(false);
    };

    // Handle resend button click
    const handleResendCode = async () => {
        if (!canResend || !email) return;

        try {
            await authService.forgotPassword(email);

            // reset states
            setCountdown(60);
            setCanResend(false);
            setResendSuccess(true);
            setCode(["", "", "", "", "", ""]);
            setError("");
            
            if (inputRefs.current[0]) {
                inputRefs.current[0].focus();
            }
            
            setTimeout(() => setResendSuccess(false), 3000);
        } catch (error) {
            setError(
                error?.response?.data?.error ||
                "Failed to resend code. Please try again."
            );
        }
    };

    //  UI Rendering
    return (
        <div className="verification-code-wrapper">
            <div className="verification-code-card">
                <img src={logo} alt="Logo" className="verification-code-logo" />

                <div className="verification-code-heading">
                    <h1 className="verification-code-title">Enter the 6-digit code</h1>
                    <p className="verification-code-subtitle">
                        Check <strong>{email}</strong> for verification code.
                    </p>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                    <div className="code-section">
                        <div className="code-inputs-container">
                            <div className="code-inputs">
                                {code.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => inputRefs.current[index] = el}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength="1"
                                        className={`code-input ${error ? 'code-input-error' : ''}`}
                                        value={digit}
                                        onChange={(e) => handleCodeChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        onPaste={index === 0 ? handlePaste : undefined}
                                        onFocus={(e) => e.target.select()}
                                        disabled={isLoading}
                                        autoFocus={index === 0}
                                    />
                                ))}
                            </div>
                        </div>

                        {error && (
                            <div className="code-error">
                                {error}
                            </div>
                        )}

                        <div className="resend-section">
                            <p className="resend-text">
                                Didn't get the code?{" "}
                                {canResend ? (
                                    <button 
                                        type="button" 
                                        className="resend-link"
                                        onClick={handleResendCode}
                                    >
                                        Resend now
                                    </button>
                                ) : (
                                    <span className="resend-countdown">
                                        Resend in {countdown} seconds
                                    </span>
                                )}
                            </p>
                            {resendSuccess && (
                                <div className="resend-success">
                                    {customMessages.resendSuccess}
                                </div>
                            )}
                        </div>

                        <div className="field">
                            <button 
                                type="submit" 
                                className="btn-submit"
                                disabled={isLoading}
                            >
                                {isLoading ? "Verifying..." : "Verify"}
                            </button>
                        </div>

                        <div className="back-to-forgot-password-section">
                            <Link to="/forgot-password" className="link-primary back-link">
                                ← Back to Forgot Password
                            </Link>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
